import { useMemo, useState } from "react";
import { Plus, Wallet, Trash2, Pencil } from "lucide-react";
import Button from "../ui/Button";
import Card from "../ui/Card";
import MetricCard from "../ui/MetricCard";
import { Table, THead, TR, TH, TD, EmptyState } from "../ui/Table";
import ConfirmDialog from "../ui/ConfirmDialog";
import { isDateInFilter } from "../layout/GlobalDateFilter";
import { formatUSD } from "../../lib/formatters";
import { formatDate } from "../../lib/dateUtils";
import DividendForm from "./DividendForm";

// Pull the unique department list from offers, clients, team, one-off
// payments — covers all the places a department string can show up.
function getDepartments(state) {
  const set = new Set();
  for (const o of state?.offers || []) if (o.department) set.add(o.department);
  for (const c of state?.clients || []) if (c.department) set.add(c.department);
  for (const m of state?.team || []) if (m.department) set.add(m.department);
  for (const p of state?.oneOffPayments || []) if (p.department) set.add(p.department);
  for (const d of state?.dividends || []) if (d.department) set.add(d.department);
  return [...set].sort();
}

export default function Dividends({ state, dashboard, dateFilter }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deptFilter, setDeptFilter] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const all = state?.dividends || [];
  const departments = useMemo(() => getDepartments(state), [state]);

  // Scope by global date filter (year + from/to month) — same pattern as
  // the rest of the dashboard so swapping years keeps everything in sync.
  const inRange = useMemo(
    () => all.filter((d) => isDateInFilter(d.date, dateFilter)),
    [all, dateFilter]
  );

  const filtered = useMemo(
    () => inRange
      .filter((d) => !deptFilter || d.department === deptFilter)
      .sort((a, b) => (b.date || "").localeCompare(a.date || "")),
    [inRange, deptFilter]
  );

  const totals = useMemo(() => {
    const total = inRange.reduce((s, d) => s + (Number(d.amount) || 0), 0);
    const byDept = {};
    for (const d of inRange) {
      const k = d.department || "(unassigned)";
      byDept[k] = (byDept[k] || 0) + (Number(d.amount) || 0);
    }
    return { total, byDept };
  }, [inRange]);

  const handleSubmit = (data) => {
    if (editing?.id) {
      dashboard.updateDividend(editing.id, data);
    } else {
      dashboard.addDividend(data);
    }
    setEditing(null);
  };

  const handleDelete = () => {
    if (confirmDelete?.id) dashboard.deleteDividend(confirmDelete.id);
    setConfirmDelete(null);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display font-semibold text-lg text-brand-ink dark:text-[var(--text-primary)]">Dividends</h2>
          <p className="text-xs text-brand-slate mt-0.5">
            Money paid out to you / partners. Subtracted from per-department Cash in Bank.
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setShowAdd(true); }}>
          <Plus size={14} /> Log dividend
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard
          label="Total in range"
          value={formatUSD(totals.total)}
          accent="amber"
          sublabel={`${inRange.length} dividend${inRange.length === 1 ? "" : "s"}`}
        />
        {Object.entries(totals.byDept).slice(0, 3).map(([dept, amt]) => (
          <MetricCard
            key={dept}
            label={dept}
            value={formatUSD(amt)}
            accent="default"
            sublabel="paid out"
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
          className={`px-3 py-1.5 border rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] dark:border-[var(--border-soft)] ${
            deptFilter ? "border-brand-blue text-brand-blue-dark font-medium" : "border-gray-200"
          }`}
        >
          <option value="">All departments</option>
          {departments.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        {deptFilter && (
          <span className="text-[11px] text-brand-slate">
            Showing {filtered.length} of {inRange.length}
          </span>
        )}
      </div>

      <Card>
        {filtered.length === 0 ? (
          <EmptyState
            icon={Wallet}
            title="No dividends logged"
            message="Click 'Log dividend' when you pay yourself or a partner. The amount will be subtracted from that department's Cash in Bank."
          />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Date</TH>
                <TH>Department</TH>
                <TH>Recipient</TH>
                <TH>Method</TH>
                <TH className="text-right">Amount</TH>
                <TH>Notes</TH>
                <TH></TH>
              </TR>
            </THead>
            <tbody>
              {filtered.map((d) => (
                <TR key={d.id}>
                  <TD>{formatDate(d.date)}</TD>
                  <TD className="font-medium">{d.department || "—"}</TD>
                  <TD>{d.recipient}</TD>
                  <TD className="text-xs text-brand-slate">{d.paymentMethod || "—"}</TD>
                  <TD className="text-right font-semibold text-amber-700 dark:text-amber-300">
                    −{formatUSD(d.amount)}
                  </TD>
                  <TD className="text-xs text-brand-slate truncate max-w-[240px]">{d.notes || "—"}</TD>
                  <TD>
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => { setEditing(d); setShowAdd(true); }}
                        className="text-brand-slate hover:text-brand-blue"
                        title="Edit"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        onClick={() => setConfirmDelete(d)}
                        className="text-brand-slate hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <DividendForm
        open={showAdd}
        onClose={() => { setShowAdd(false); setEditing(null); }}
        onSubmit={handleSubmit}
        initial={editing}
        departments={departments}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title="Delete dividend?"
        message={confirmDelete ? `Remove the ${formatUSD(confirmDelete.amount)} dividend from ${formatDate(confirmDelete.date)}? Cash in Bank for ${confirmDelete.department || "this department"} will increase by that amount.` : ""}
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}
